import React, { useContext, useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import { ComponentContext } from '../../context/componentContext';
import { AuthContext } from '../../context/authContext';
import { StartFarmContext } from '../../context/startFarmContext';
import { PaymentContext } from '../../context/paymentContext';

import AuthHeader from '../../container/Layout/AuthHeader';
import Footer from '../../components/starFarm/FarmExperience/Footer';
import UnexpectedError from '../../components/UnexpectedError';
import FarmPackage from '../../components/starFarm/FarmExperience/FarmPackage';
import FarmConfigure from '../../components/starFarm/FarmExperience/FarmConfigure';
import FarmPay from '../../components/starFarm/FarmExperience/FarmPay';
import FarmConfirm from '../../components/starFarm/FarmExperience/FarmConfirm';
import FarmContract from '../../components/starFarm/Modal/FarmContract';
import FarmDetails from '../../components/starFarm/Modal/FarmDetails';
import Progress from '../../components/starFarm/FarmExperience/Progress';

const StartFarm = ({ history, location: { search, state } }) => {
  document.title = 'CrowdFarmer | Start Farming';

  const {
    reload,
    unexpectedError,
    setUnexpectedError,
    setLoading,
    runPackage,
    setRunPackage
  } = useContext(ComponentContext);
  const { session, setSession, isAuthenticated } = useContext(AuthContext);
  const { initializePayment, verifyPayment, convertUSDtoGHS, saveOrder } = useContext(
    PaymentContext
  );
  const {
    steps,
    setActiveStep,
    selectedPackage,
    handleSelection,
    handleNext,
    handleBack,
    activeStep,
    handleChange,
    setSelectedAcreage,
    selectedAcreage,
    setCost,
    cost,
    setSelectedCycle,
    selectedCycle
  } = useContext(StartFarmContext);

  const { firstName } = isAuthenticated().user;

  const [selectedPayment, setSelectedPayment] = useState('creditCard');
  const [product, setProduct] = useState({});
  const [order, setOrder] = useState({});
  const [error, setError] = useState(null);

  const choosePaymentMethod = method => {
    setSelectedPayment(method);
  };

  const handlePaystackPay = async () => {
    if (session)
      try {
        setLoading(true);
        const fixerData = await convertUSDtoGHS(cost);
        const data = {
          typeof: 'paystack',
          product: product._id,
          package: selectedPackage.title,
          crop: product.crop.name,
          cycle: selectedCycle,
          acreage: selectedAcreage,
          projectedReturn:
            (selectedAcreage * product.pricePerAcre * product.projectedReturnsPerAcre) / 100,
          projectedYield: selectedAcreage * product.yieldPerAcre,
          usdRate: fixerData.rateUsed,
          amountToCharge: parseFloat(fixerData.newAmount / 0.9805).toFixed(2) * 1, //paystack charges included
          cost
        };
        const result = await initializePayment(data);
        if (result.code === 200) {
          window.onbeforeunload = null;
          window.location.href = result.data.authorization_url;
        } else setError('Unexpected Server Error!');
        setLoading(false);
      } catch (error) {
        if (error && error.status === 401) setSession(false);
        else setUnexpectedError('Unexpected Network Error!');
      }
  };

  const handlePaylater = async () => {
    window.onbeforeunload = null;
    if (session)
      try {
        const data = {
          typeof: 'paylater',
          product: product._id,
          package: selectedPackage.title,
          crop: product.crop.name,
          cycle: selectedCycle,
          acreage: selectedAcreage,
          projectedReturn: (cost * product.projectedReturnsPerAcre) / 100,
          projectedYield: selectedAcreage * product.yieldPerAcre,
          cost
        };
        setLoading(true);
        const result = await saveOrder(data);
        setLoading(false);
        if (result.code === 200) {
          setOrder(result.data);
          setActiveStep(3);
        } else setError(result.data.message);
      } catch (error) {
        if (error && error.status === 401) setSession(false);
        else setUnexpectedError('Unexpected Network Error!');
      }
  };

  sessionStorage.setItem('ran_verify', false);

  useEffect(() => {
    setError(null);
    let mounted = true;
    if (session) {
      const reference = search.split('reference=')[1];
      if (reference) {
        let params = new URLSearchParams(search);
        params.delete('trxref');
        params.delete('reference');
        window.history.replaceState(null, '', '?' + params);
        if (!JSON.parse(sessionStorage.getItem('ran_verify'))) {
          (async () => {
            setLoading(true);
            try {
              const response = await verifyPayment(reference);
              if (mounted)
                if (response.code === 200) {
                  setOrder(response.data);
                  setActiveStep(3);
                } else setError(response.data.message);
              sessionStorage.setItem('ran_verify', true);
            } catch (error) {
              if (error && error.status === 401) setSession(false);
              else setUnexpectedError('Unexpected Network Error!');
            }
            setLoading(false);
          })();
        }
      } else {
        if (state && state.step && state.product && mounted) {
          setProduct(state.product);
          setActiveStep(state.step);
          handleSelection(state.product.crop.package);
          delete state.product;
          delete state.step;
          history.replace({ state: {} });
        } else setRunPackage(true);
      }
    }
    return () => (mounted = false);
  }, [
    reload,
    handleSelection,
    history,
    state,
    session,
    setActiveStep,
    setLoading,
    setSession,
    setUnexpectedError,
    verifyPayment,
    search,
    setRunPackage
  ]);

  const getStepContent = step => {
    switch (step) {
      case 0:
        return (
          <FarmPackage
            handleSelection={handleSelection}
            selectedPackage={selectedPackage}
            setUnexpectedError={setUnexpectedError}
            setLoading={setLoading}
            runPackage={runPackage}
            firstName={firstName}
          />
        );
      case 1:
        return (
          <FarmConfigure
            setProduct={setProduct}
            product={product}
            selectedPackage={selectedPackage}
            selectedAcreage={selectedAcreage}
            setSelectedAcreage={setSelectedAcreage}
            setCost={setCost}
            setSelectedCycle={setSelectedCycle}
            handleChange={handleChange}
            setLoading={setLoading}
            setUnexpectedError={setUnexpectedError}
            session={session}
            setSession={setSession}
          />
        );
      case 2:
        return (
          <FarmPay
            cost={cost}
            vat={product.vat}
            selectedPackage={selectedPackage}
            selectedPayment={selectedPayment}
            choosePaymentMethod={choosePaymentMethod}
          />
        );
      case 3:
        return (
          <FarmConfirm
            order={order}
            cost={cost}
            vat={product.vat}
            packageTitle={(selectedPackage && selectedPackage.title) || ''}
            token={isAuthenticated().token}
            firstName={firstName}
            setUnexpectedError={setUnexpectedError}
            setSession={setSession}
          />
        );
      default:
        return null;
    }
  };

  if (!isAuthenticated()) return <Redirect to={'/unauthorized'} />;

  window.onbeforeunload = function(event) {
    event.returnValue = 'Unsafed data maybe lost.';
  };

  return (
    <div className="relative overflow-hidden">
      <FarmContract />
      {Object.keys(product).length > 0 && <FarmDetails product={product} />}
      <AuthHeader startFarm />
      <div className="bg-cf-200 pt-20">
        <ul className="md:hidden flex flex-wrap items-center px-3 md:px-0 my-6">
          {steps.map((step, i) => (
            <li
              key={i}
              className={
                activeStep >= i
                  ? 'flex items-center text-cf-dark font-bold'
                  : 'flex items-center px-1 md:px-2'
              }
            >
              <Progress step={step} activeStep={activeStep} i={i} />
            </li>
          ))}
        </ul>
        <div className="container mx-auto">
          <div className="w-full lg:w-4/5 mx-auto bg-white rounded p-10">
            <div className="w-full md:w-127 mx-auto">
              {!error && !unexpectedError ? (
                <>
                  {getStepContent(activeStep)}
                  {Object.keys(order).length > 0 ? (
                    <div className="w-80 mx-auto mt-8">
                      <a href="/home">
                        <button
                          onClick={() => {
                            window.onbeforeunload = null;
                          }}
                          className="w-full mx-auto text-gray-100 bg-cf-300 font-header py-2 px-8 shadow rounded focus:outline-none"
                        >
                          Continue to Dashboard
                        </button>
                      </a>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mt-6">
                      <button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        className={`font-header ${
                          activeStep === 0
                            ? 'text-gray-500 bg-gray-300 cursor-not-allowed'
                            : 'text-cf-300 bg-cf-200'
                        } py-2 px-8 shadow rounded focus:outline-none`}
                      >
                        Back
                      </button>
                      {activeStep <= 1 && (
                        <button
                          disabled={!selectedPackage}
                          onClick={activeStep === steps.length - 1 ? '' : handleNext}
                          className={`font-header ${
                            !selectedPackage
                              ? 'text-gray-500 bg-gray-300 cursor-not-allowed'
                              : 'text-gray-200 bg-cf-300'
                          } py-2 px-8 shadow rounded focus:outline-none`}
                        >
                          Next
                        </button>
                      )}

                      {activeStep === steps.length - 2 && selectedPayment === 'creditCard' && (
                        <button
                          onClick={handlePaystackPay}
                          className="font-header text-gray-200 bg-cf-300 py-2 px-8 shadow rounded focus:outline-none"
                        >
                          Proceed
                        </button>
                      )}

                      {activeStep === steps.length - 2 && selectedPayment === 'transfer' && (
                        <button
                          onClick={handlePaylater}
                          className="font-header text-gray-200 bg-cf-300 py-2 px-8 shadow rounded focus:outline-none"
                        >
                          Save & Print
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <UnexpectedError error={error} unexpectedError={unexpectedError} />
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default StartFarm;
